import React, { useEffect, useRef } from 'react';

const GoldenLightsBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions to match window size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Define particle properties
    interface GoldenLight {
      x: number;
      y: number;
      size: number;
      maxSize: number;
      speedX: number;
      speedY: number;
      opacity: number;
      opacityStep: number;
      color: string;
    }

    // Create golden lights
    const lights: GoldenLight[] = [];
    const lightCount = 100; // More lights for better effect

    // Gold color variations with higher intensity
    const goldColors = [
      '255, 215, 0',    // Gold
      '255, 223, 0',    // Light gold
      '218, 165, 32',   // Goldenrod
      '255, 248, 220',  // Cornsilk
      '240, 230, 140',  // Khaki gold
      '212, 175, 55',   // Metallic gold
    ];

    // Initialize lights
    for (let i = 0; i < lightCount; i++) {
      const maxSize = Math.random() * 200 + 150; // Larger size for more visible effect
      lights.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * maxSize,
        maxSize: maxSize,
        speedX: (Math.random() - 0.5) * 0.6, // Slower movement for dreamy effect
        speedY: (Math.random() - 0.5) * 0.6,
        opacity: Math.random() * 0.6 + 0.3, // Higher base opacity
        opacityStep: (Math.random() * 0.01 + 0.005) * (Math.random() > 0.5 ? 1 : -1),
        color: goldColors[Math.floor(Math.random() * goldColors.length)]
      });
    }

    // Animation function
    const animate = () => {
      // Clear canvas with slight fade effect for trails
      ctx.fillStyle = 'rgba(18, 18, 18, 0.15)'; // More transparent for longer trails
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw each light
      lights.forEach(light => {
        // Move the light
        light.x += light.speedX;
        light.y += light.speedY;

        // Bounce off edges with some margin
        if (light.x < -light.size) light.x = canvas.width + light.size;
        if (light.x > canvas.width + light.size) light.x = -light.size;
        if (light.y < -light.size) light.y = canvas.height + light.size;
        if (light.y > canvas.height + light.size) light.y = -light.size;

        // Pulse size
        light.size += Math.sin(Date.now() * 0.0008) * 0.8;
        if (light.size < 0) light.size = 0;
        if (light.size > light.maxSize) light.size = light.maxSize;

        // Change opacity
        light.opacity += light.opacityStep;
        if (light.opacity > 0.9 || light.opacity < 0.3) {
          light.opacityStep *= -1;
        }

        // Draw the light with gradient
        const gradient = ctx.createRadialGradient(
          light.x, light.y, 0,
          light.x, light.y, light.size
        );

        gradient.addColorStop(0, `rgba(${light.color}, ${light.opacity * 1.5})`);
        gradient.addColorStop(0.4, `rgba(${light.color}, ${light.opacity * 0.8})`);
        gradient.addColorStop(0.7, `rgba(${light.color}, ${light.opacity * 0.4})`);
        gradient.addColorStop(1, `rgba(${light.color}, 0)`);

        ctx.beginPath();
        ctx.fillStyle = gradient;
        ctx.arc(light.x, light.y, light.size, 0, Math.PI * 2);
        ctx.fill();

        // Add a small bright center for sparkle
        const centerSize = light.size * 0.08;
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 255, 255, ${light.opacity * 1.8})`;
        ctx.arc(light.x, light.y, centerSize, 0, Math.PI * 2);
        ctx.fill();
      });

      // Continue animation
      requestAnimationFrame(animate);
    };

    // Start animation
    const animationId = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      window.removeEventListener('resize', setCanvasSize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none" style={{ zIndex: -1 }}>
      {/* Dark background with slight transparency */}
      <div className="absolute inset-0 bg-black opacity-70"></div>
      
      {/* Canvas for animated lights */}
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0"
        style={{ display: 'block', zIndex: 0 }}
      />
      
      {/* Static glow elements for additional depth */}
      <div className="absolute top-1/4 left-1/4 w-[700px] h-[700px] rounded-full bg-gold/15 blur-[180px] animate-pulse-slow opacity-60"></div>
      <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-gold/15 blur-[150px] animate-pulse-slow opacity-50" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-3/4 right-1/3 w-[500px] h-[500px] rounded-full bg-gold/15 blur-[120px] animate-pulse-slow opacity-40" style={{ animationDelay: '1s' }}></div>
      <div className="absolute bottom-1/3 left-1/3 w-[550px] h-[550px] rounded-full bg-gold/15 blur-[140px] animate-pulse-slow opacity-45" style={{ animationDelay: '3s' }}></div>
    </div>
  );
};

export default GoldenLightsBackground; 